import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { ChevronRight, ChevronDown, MapPin, Loader2, Globe, Building2, CheckCircle, Clock, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const statusColor = (s) => {
  if (s === "completed") return "text-emerald-500";
  if (s === "in_progress" || s === "seeded") return "text-sky-500";
  return "text-slate-400";
};
const statusBg = (s) => {
  if (s === "completed") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s === "in_progress" || s === "seeded") return "bg-sky-50 text-sky-700 border-sky-200";
  return "bg-slate-50 text-slate-600 border-slate-200";
};
const regionColor = (r) => {
  if (r === "NorCal") return "bg-blue-100 text-blue-700";
  if (r === "Central California") return "bg-amber-100 text-amber-700";
  if (r === "SoCal") return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-600";
};

export default function GeoDrilldownPanel() {
  const [summary, setSummary] = useState(null);
  const [states, setStates] = useState([]);
  const [expandedState, setExpandedState] = useState(null);
  const [counties, setCounties] = useState([]);
  const [expandedCounty, setExpandedCounty] = useState(null);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const [sumRes, stRes] = await Promise.all([
        axios.get(`${API}/admin/geo/summary`),
        axios.get(`${API}/admin/geo/states`),
      ]);
      setSummary(sumRes.data);
      setStates(stRes.data.states || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const loadCounties = async (abbr) => {
    if (expandedState === abbr) { setExpandedState(null); setCounties([]); return; }
    setExpandedState(abbr);
    setExpandedCounty(null);
    setCities([]);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/geo/states/${abbr}/counties`);
      setCounties(res.data.counties || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadCities = async (fips) => {
    if (expandedCounty === fips) { setExpandedCounty(null); setCities([]); return; }
    setExpandedCounty(fips);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/geo/counties/${fips}/cities`);
      setCities(res.data.cities || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const triggerSeed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/admin/geo/seed`);
      toast.success("Census seeding started in background");
    } catch (e) { toast.error("Failed to start seeding"); }
    setSeeding(false);
  };

  const triggerRepair = async () => {
    try {
      await axios.post(`${API}/admin/geo/repair-ca`);
      toast.success("California data repair started");
    } catch (e) { toast.error("Repair failed"); }
  };

  if (!summary) return <div className="p-4 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="geo-drilldown-panel">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-1">States</div>
          <div className="text-xl font-bold text-slate-800">{summary.states.total}</div>
          <div className="text-xs text-slate-400 mt-1">
            {summary.states.in_progress} active, {summary.states.complete} done
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-1">Counties</div>
          <div className="text-xl font-bold text-slate-800">{summary.counties.total}</div>
          <div className="text-xs text-slate-400 mt-1">{summary.counties.complete} complete</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-1">Cities (pop 1K+)</div>
          <div className="text-xl font-bold text-slate-800">{summary.cities.total}</div>
          <div className="text-xs text-slate-400 mt-1">
            {summary.cities.mined} mined, {summary.cities.low_coverage} low coverage
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={triggerSeed} disabled={seeding} className="text-xs px-3 py-1.5 bg-slate-800 text-white rounded-md hover:bg-slate-700 disabled:opacity-50 flex items-center gap-1" data-testid="seed-census-btn">
          {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
          Seed Census Data
        </button>
        <button onClick={triggerRepair} className="text-xs px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-500 flex items-center gap-1" data-testid="repair-ca-btn">
          <RefreshCw className="w-3 h-3" /> Repair CA Data
        </button>
        <button onClick={fetchSummary} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* State List */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 text-xs font-medium text-slate-500 grid grid-cols-[1fr,80px,80px,100px] gap-2">
          <span>State</span><span className="text-right">Pop</span><span className="text-right">Counties</span><span className="text-right">Status</span>
        </div>
        <div className="max-h-[300px] overflow-y-auto divide-y divide-slate-100">
          {states.map((s) => (
            <div key={s.fips_code}>
              <button onClick={() => loadCounties(s.abbreviation)} className="w-full px-4 py-2 text-left grid grid-cols-[1fr,80px,80px,100px] gap-2 items-center hover:bg-slate-50 transition-colors" data-testid={`state-row-${s.abbreviation}`}>
                <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                  {expandedState === s.abbreviation ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  {s.name}
                  <span className="text-xs text-slate-400">{s.abbreviation}</span>
                </span>
                <span className="text-xs text-slate-500 text-right">{(s.population / 1e6).toFixed(1)}M</span>
                <span className="text-xs text-slate-500 text-right">{s.counties_total}</span>
                <span className={`text-xs text-right px-2 py-0.5 rounded-full border ${statusBg(s.mining_status)}`}>
                  {s.mining_status}
                </span>
              </button>

              {/* Counties Drill-Down */}
              {expandedState === s.abbreviation && (
                <div className="bg-slate-50/50 border-t border-slate-100">
                  {loading && counties.length === 0 ? (
                    <div className="p-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-400" /></div>
                  ) : counties.length === 0 ? (
                    <div className="p-3 text-center text-xs text-slate-400">No counties seeded yet</div>
                  ) : (
                    <div className="max-h-[250px] overflow-y-auto">
                      {counties.map((c) => (
                        <div key={c.fips_code}>
                          <button onClick={() => loadCities(c.fips_code)} className="w-full pl-8 pr-4 py-1.5 text-left grid grid-cols-[1fr,60px,50px,80px] gap-2 items-center hover:bg-white/60 transition-colors" data-testid={`county-row-${c.fips_code}`}>
                            <span className="flex items-center gap-2 text-xs text-slate-700">
                              {expandedCounty === c.fips_code ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              <Building2 className="w-3 h-3 text-slate-400" />
                              {c.name.replace(`, ${s.name}`, "").replace(" County", "")}
                              {c.region && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${regionColor(c.region)}`}>{c.region}</span>}
                            </span>
                            <span className="text-[10px] text-slate-400 text-right">{(c.population / 1e3).toFixed(0)}K</span>
                            <span className="text-[10px] text-slate-400 text-right">{c.cities_total} cities</span>
                            <span className={`text-[10px] text-right ${statusColor(c.mining_status)}`}>
                              {c.mining_status}
                            </span>
                          </button>

                          {/* Cities Drill-Down */}
                          {expandedCounty === c.fips_code && (
                            <div className="bg-white/40 border-t border-slate-100/50">
                              {loading && cities.length === 0 ? (
                                <div className="p-2 text-center"><Loader2 className="w-3 h-3 animate-spin mx-auto text-slate-400" /></div>
                              ) : cities.length === 0 ? (
                                <div className="p-2 text-center text-[10px] text-slate-400">No cities mapped to this county</div>
                              ) : (
                                <div className="max-h-[200px] overflow-y-auto divide-y divide-slate-50">
                                  {cities.map((city, i) => (
                                    <div key={i} className="pl-14 pr-4 py-1 grid grid-cols-[1fr,60px,60px,60px] gap-2 items-center text-[10px]">
                                      <span className="flex items-center gap-1 text-slate-600">
                                        <MapPin className="w-2.5 h-2.5 text-slate-300" />
                                        {city.name?.split(",")[0]}
                                      </span>
                                      <span className="text-slate-400 text-right">{((city.census_population || city.population || 0) / 1e3).toFixed(1)}K</span>
                                      <span className={`text-right ${(city.coverage_confidence || 0) >= 75 ? "text-emerald-500" : (city.coverage_confidence || 0) > 0 ? "text-amber-500" : "text-slate-300"}`}>
                                        {city.coverage_confidence || 0}%
                                      </span>
                                      <span className={`text-right ${statusColor(city.mining_status)}`}>
                                        {city.mining_status || "pending"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
